import { recommendationsApi } from "@/services/api/recommendationsApi";
import algoliaSearch from "algoliasearch/lite";
import { paramsHelper } from "../../components/mixin/paramsHelper";
import FavoriteApi from "@/services/api/favoritesApi";
import { mapGetters } from "vuex";
import { breakPointsHelper } from "../../components/mixin/breakPointsHelpers";

const FavoriteApiService = new FavoriteApi();

export const favoritesPageHelper = {
  mixins: [paramsHelper, breakPointsHelper],
  data() {
    return {
      algoliaClient: algoliaSearch(this.$env.algolia().appId, this.$env.algolia().token, {
        timeouts: {
          connect: this.$utils.constants.ALGOLIA_CONNECT_TIMEOUT,
          read: this.$utils.constants.ALGOLIA_READ_TIMEOUT,
        },
      }),
      recommendedBrandIds: [],
      favoriteProductObjects: [],
      favoriteBrandsLoading: false,
      favoriteProductsLoading: false,
      recommendedBrandsLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      favoriteProductIds: "favoriteProductsState",
      newRecommendationCount: "newRecommendationCount",
      favoriteBrandIds: "favoritesState",
    }),
    recommendedBrands() {
      return this.brands?.filter((brand) => this.recommendedBrandIds.includes(brand.id));
    },
    favoriteBrands() {
      return this.brands?.filter((brand) => this.favoriteBrandIds.includes(brand.id));
    },
    favoriteProducts() {
      return this.favoriteProductObjects
        .filter((item) => this.favoriteProductIds?.includes(item.id))
        .map((item) => {
          return { ...item, is_fav: true };
        });
    },
    isMobile() {
      return this.underBreakpoint("sm");
    },
    sortBy() {
      return this?.sortingOptions?.find((item) => item?.value === this.$route?.query?.sortBy);
    },
    searchKeyword() {
      return this.$route?.query?.keyword;
    },
  },
  mounted() {
    this.ackNewRecommendations();
  },
  methods: {
    getRecommendedBrandIds,
    getFavoriteBrandIds,
    getFavoriteProducts,
    fetchBrandObjects,
    sortBrands,
    filterBrandsByKeyword,
    ackNewRecommendations,
    setDefaultSorting,
  },
};

async function getRecommendedBrandIds() {
  try {
    this.recommendedBrandsLoading = true;
    const retailerId = this.$session.getUserInfo().retailer_id;
    const { data } = await recommendationsApi.fetchRecommendedBrands(retailerId);
    const { brand_ids } = data || {};
    this.recommendedBrandIds = brand_ids || [];
  } catch (error) {
    this.$logger?.notify(new Error(error));
  } finally {
    this.recommendedBrandsLoading = false;
  }
}

async function getFavoriteBrandIds() {
  try {
    const { data } = await FavoriteApiService.getFavoriteBrands();
    this.$store.commit("setFavoriteState", data?.brand_ids || []);
  } catch (error) {
    this.$logger.notify(new Error(error));
  }
}

async function getFavoriteProducts() {
  try {
    this.favoriteProductsLoading = true;
    const productIds = await this.$utils.getFavoriteProducts.call(this);
    const productsIndex = this.$env.getOrigin() + "_products";

    const products = await this.algoliaClient.initIndex(productsIndex).getObjects(productIds);
    this.favoriteProductObjects = products.results || [];
  } catch (error) {
    this.$logger?.notify(new Error(error));
  } finally {
    this.favoriteProductsLoading = false;
  }
}

async function fetchBrandObjects() {
  try {
    this.favoriteBrandsLoading = true;
    this.recommendedBrandsLoading = true;
    await Promise.all([this.getRecommendedBrandIds(), this.getFavoriteBrandIds()]);

    const brandIds = [
      ...this.recommendedBrandIds,
      ...(this.favoriteBrandIds.filter((brandId) => !this.recommendedBrandIds.includes(brandId)) || []),
    ];
    const brandsIndex = this.$env.getOrigin() + "_brands";
    const brands = await this.algoliaClient.initIndex(brandsIndex).getObjects(brandIds);
    this.brands = brands?.results || [];
  } catch (error) {
    this.$logger.notify(new Error(error));
  } finally {
    this.favoriteBrandsLoading = false;
    this.recommendedBrandsLoading = false;
  }
}

function sortBrands(brands) {
  const sortDataKey = this.sortBy?.value;

  if (sortDataKey === "most_recently_followed" || sortDataKey === "most_recently_added") {
    return brands;
  }

  const sortDirection = this.sortBy?.direction;

  const sortedBrands = brands.sort((aBrand, bBrand) => {
    return sortDataKey === "made_active"
      ? new Date(bBrand[sortDataKey]) - new Date(aBrand[sortDataKey])
      : bBrand[sortDataKey] - aBrand[sortDataKey];
  });

  return sortDirection === "ASC" ? sortedBrands?.reverse() : sortedBrands;
}

function filterBrandsByKeyword(brands) {
  return brands?.filter((brand) =>
    this.searchKeyword ? brand.brand_name?.toLowerCase().includes(this.searchKeyword?.toLowerCase()) : true
  );
}

async function ackNewRecommendations() {
  if (
    this.newRecommendationCount &&
    (this.$route.path === "/favorites" || this.$route.path === "/favorites/recommended-brands")
  ) {
    try {
      this.$store.commit("setNewRecommendationCount", 0);
      await FavoriteApiService.ackNewRecommendation();
    } catch (error) {
      this.$bugsnagClient.notify(new Error(error));
    }
  }
}

function setDefaultSorting() {
  if (!this.$route.query.sortBy) {
    this.updateURLParams("sortBy", this.sortingOptions[0].value);
  }
}
